import { useCallback, useEffect, useRef, useState } from 'react'
import type { VelocidadeTimeline } from '../dominio/didatico/timelineCinematica'

/** Segundos que a sequência inteira leva em 1×. Lento de propósito: é aula. */
const DURACAO_BASE_S = 4.5

export interface Timeline {
  tempo: number
  reproduzindo: boolean
  velocidade: VelocidadeTimeline
  alternarReproducao: () => void
  reiniciar: () => void
  irPara: (t: number) => void
  voltarEtapa: (marcos?: readonly number[]) => void
  avancarEtapa: (marcos?: readonly number[]) => void
  definirVelocidade: (v: VelocidadeTimeline) => void
}

/**
 * Reprodução da timeline em 0..1.
 *
 * A câmera não é tocada aqui: pausar e orbitar precisa funcionar sem mexer na
 * posição temporal, então tempo e câmera são estados independentes.
 */
export function useTimeline(): Timeline {
  const [tempo, setTempo] = useState(0)
  const [reproduzindo, setReproduzindo] = useState(false)
  const [velocidade, setVelocidade] = useState<VelocidadeTimeline>(0.5)
  const ultimoQuadro = useRef<number | null>(null)

  useEffect(() => {
    if (!reproduzindo) {
      ultimoQuadro.current = null
      return
    }
    let id = 0
    const passo = (agora: number) => {
      const anterior = ultimoQuadro.current ?? agora
      ultimoQuadro.current = agora
      const delta = (agora - anterior) / 1000
      setTempo((atual) => {
        const proximo = atual + (delta / DURACAO_BASE_S) * velocidade
        if (proximo >= 1) {
          setReproduzindo(false)
          return 1
        }
        return proximo
      })
      id = requestAnimationFrame(passo)
    }
    id = requestAnimationFrame(passo)
    return () => cancelAnimationFrame(id)
  }, [reproduzindo, velocidade])

  const alternarReproducao = useCallback(() => {
    setReproduzindo((v) => {
      // reproduzir no fim recomeça, em vez de não fazer nada
      if (!v) setTempo((t) => (t >= 0.999 ? 0 : t))
      return !v
    })
  }, [])

  const reiniciar = useCallback(() => {
    setReproduzindo(false)
    setTempo(0)
  }, [])

  const irPara = useCallback((t: number) => {
    setReproduzindo(false)
    setTempo(Math.max(0, Math.min(1, t)))
  }, [])

  /* Fallback neutro. Cada técnica pode fornecer seus próprios marcos sem
     acoplar este hook ao domínio de extração. */
  const marcosPadrao = [0, 0.25, 0.5, 0.75, 1] as const
  const voltarEtapa = useCallback((marcos: readonly number[] = marcosPadrao) => {
    setReproduzindo(false)
    setTempo((atual) => [...marcos].reverse().find((marco) => marco < atual - 0.01) ?? 0)
  }, [])
  const avancarEtapa = useCallback((marcos: readonly number[] = marcosPadrao) => {
    setReproduzindo(false)
    setTempo((atual) => marcos.find((marco) => marco > atual + 0.01) ?? 1)
  }, [])

  return {
    tempo,
    reproduzindo,
    velocidade,
    alternarReproducao,
    reiniciar,
    irPara,
    voltarEtapa,
    avancarEtapa,
    definirVelocidade: setVelocidade,
  }
}
